// ANANT LEADGEN — Dashboard + Landing Page Editor
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles, Users, TrendingUp, Eye, Lock, Zap, Copy, ExternalLink, Loader2, Award, Send, Filter, MessageCircle, X } from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';

// ===== Plan Timer (sticky bar): shows plan label + days/hours remaining + progress bar =====
function PlanTimer({ sub }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  if (!sub || !sub.active || !sub.expires_at) {
    return (
      <div className="flex-1 min-w-0 px-2 py-1 rounded-lg border border-amber-400/40 bg-amber-500/10 text-[10px] font-black text-amber-200 truncate text-center">
        🔒 No Active Plan · Subscribe below
      </div>
    );
  }
  try {
    const exp = new Date(sub.expires_at).getTime();
    const diff = exp - now;
    const days = Math.floor(diff / 86400000);
    const hrs = Math.floor((diff % 86400000) / 3600000);
    const total = sub.plan_id === 'yearly' ? 365 : 30;
    const pct = Math.max(0, Math.min(100, (diff / (total * 86400000)) * 100));
    const critical = days < 3;
    return (
      <div className={`flex-1 min-w-0 rounded-lg border-2 px-2 py-1 ${critical ? 'border-rose-400 bg-rose-500/15 animate-pulse' : 'border-emerald-400/50 bg-emerald-500/10'}`} data-testid="leadgen-plan-timer">
        <div className="flex items-center justify-between gap-2">
          <div className={`text-[9px] font-black uppercase tracking-widest ${critical ? 'text-rose-200' : 'text-emerald-200'} truncate`}>
            ⏳ {sub.plan_label || 'Active'} · {days > 0 ? `${days}d ${hrs}h` : `${hrs}h`} left
          </div>
          <div className="text-[9px] opacity-70 shrink-0 hidden sm:block">{new Date(sub.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</div>
        </div>
        <div className="mt-0.5 h-1 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full ${critical ? 'bg-rose-400' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  } catch { return null; }
}


export default function LeadGen() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [lp, setLp] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [tab, setTab] = useState('dashboard');

  // LP form state
  const [displayName, setDisplayName] = useState('');
  const [brandDesc, setBrandDesc] = useState('');
  const [heroUrl, setHeroUrl] = useState('');
  const [hookOffer, setHookOffer] = useState('');
  const [customCategories, setCustomCategories] = useState('');
  const [theme, setTheme] = useState('gold');

  const activeSub = config?.subscription?.active;
  const publicUrl = useMemo(() => (lp?.slug ? `${window.location.origin}/leadgen/lp/${lp.slug}` : ''), [lp]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m, s, l, lb] = await Promise.all([
        api.get('/leadgen/config'),
        api.get('/leadgen/my-landing-page'),
        api.get('/leadgen/my-stats'),
        api.get('/leadgen/my-leads'),
        api.get('/leadgen/leaderboard'),
      ]);
      setConfig(c.data);
      const mLp = m.data?.landing_page;
      setLp(mLp);
      if (mLp) {
        setDisplayName(mLp.display_name || '');
        setBrandDesc(mLp.brand_description || '');
        setHeroUrl(mLp.hero_image_url || '');
        setHookOffer(mLp.hook_offer || '');
        setCustomCategories((mLp.custom_categories || []).join(', '));
        setTheme(mLp.theme || 'gold');
      }
      setStats(s.data);
      setLeads(l.data?.leads || []);
      setLeaderboard(lb.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const buyPlan = async (planId) => {
    if (!window.confirm(`${planId === 'monthly' ? '300 AT' : '3000 AT'} deduct hoga aur 7-level Affiliate Network me distribute hoga. Confirm?`)) return;
    setSubscribing(true);
    try {
      const r = await api.post('/leadgen/subscribe', { plan_id: planId });
      toast.success(r.data.message);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscribe failed'); }
    finally { setSubscribing(false); }
  };

  const saveLP = async () => {
    if (!displayName.trim()) { toast.error('Display name is required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/leadgen/landing-page', {
        display_name: displayName.trim(),
        brand_description: brandDesc.trim() || null,
        hero_image_url: heroUrl.trim() || null,
        hook_offer: hookOffer.trim() || null,
        custom_categories: customCategories.split(',').map((s) => s.trim()).filter(Boolean),
        theme,
      });
      toast.success('Landing page saved 🎉');
      setLp(r.data.landing_page);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const copyLpUrl = () => { navigator.clipboard.writeText(publicUrl).then(() => toast.success('URL copied!')); };

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-slate-950 via-slate-900 to-black text-white">
      <div className="sticky top-16 z-40 bg-slate-950 backdrop-blur-xl border-b-2 border-cyan-400/50 px-4 py-2 flex items-center justify-between shadow-2xl shadow-black gap-2">
        <Link to="/" className="text-xs text-slate-400 hover:text-white shrink-0">← Home</Link>
        <PlanTimer sub={config?.subscription} />
        <ATWalletBadge compact />
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-[10px] font-black tracking-widest text-cyan-200 mb-3">
            <Sparkles className="w-3 h-3" /> ANANT ECOSYSTEM · NEW TOOL
          </div>
          <h1 className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">Anant LeadGen</h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl mx-auto">Turn visitors into leads with your own <b className="text-cyan-300">mini landing page</b> + smart form + QR + 7-level Affiliate Network.</p>
        </div>

        {!activeSub ? (
          <div className="rounded-3xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-950/40 to-blue-950/40 p-6" data-testid="leadgen-paywall">
            <div className="flex items-center gap-2 mb-3"><Lock className="w-5 h-5 text-cyan-400" /><div className="text-lg font-black">Subscribe to Create Your Landing Page</div></div>
            <div className="text-sm text-slate-300 mb-4">33% Affiliate Network to upline · 50% admin · Instant activation.</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(config?.plans || []).map((p) => (
                <button key={p.id} data-testid={`leadgen-plan-${p.id}`} disabled={subscribing} onClick={() => buyPlan(p.id)}
                  className={`text-left rounded-2xl border-2 p-4 hover:scale-[1.01] ${p.id === 'yearly' ? 'border-emerald-400/60 bg-emerald-900/30' : 'border-cyan-400/50 bg-cyan-900/30'}`}>
                  <div className="flex items-center justify-between"><div className="text-xs opacity-70 uppercase tracking-widest">{p.label}</div>{p.id === 'yearly' && <div className="text-[9px] font-black px-2 py-0.5 rounded-full bg-emerald-500 text-black">BEST</div>}</div>
                  <div className="text-3xl font-black mt-1">{p.amount} <span className="text-lg opacity-70">AT</span></div>
                  <div className="text-xs opacity-80 mt-1">{p.highlight} · {p.days} days</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-black text-cyan-200"><Zap className="w-3 h-3" /> Tap to Subscribe</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-1 mb-4 flex-wrap" data-testid="leadgen-tabs">
              {[['dashboard', '📊 Dashboard'], ['landing', '🎨 My Landing Page'], ['leads', '👥 My Leads'], ['board', '🏆 Leaderboard']].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`leadgen-tab-${k}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${tab === k ? 'bg-cyan-500 text-black' : 'bg-white/5 text-slate-300'}`}>{l}</button>
              ))}
            </div>

            {tab === 'dashboard' && stats && (
              <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="leadgen-stats">
                {[
                  { icon: <Users />, label: 'Total Leads', value: stats.total_leads, color: 'cyan' },
                  { icon: <TrendingUp />, label: 'Today', value: stats.today_leads, color: 'emerald' },
                  { icon: <Award />, label: 'This Month', value: stats.month_leads, color: 'amber' },
                  { icon: <Eye />, label: 'Profile Views', value: stats.profile_views, color: 'fuchsia' },
                ].map((s, i) => (
                  <div key={i} className={`rounded-xl border-2 border-${s.color}-400/40 bg-${s.color}-500/10 p-3`}>
                    <div className={`text-${s.color}-300`}>{s.icon}</div>
                    <div className="text-[9px] uppercase tracking-widest opacity-70 mt-1">{s.label}</div>
                    <div className="text-2xl font-black">{s.value}</div>
                  </div>
                ))}
                <div className="col-span-2 md:col-span-4 rounded-xl border-2 border-fuchsia-400/40 bg-fuchsia-500/10 p-3">
                  <div className="text-[9px] uppercase tracking-widest opacity-70">Conversion Rate</div>
                  <div className="text-3xl font-black text-fuchsia-300">{stats.conversion_pct}%</div>
                  <div className="text-[10px] opacity-70">Leads / Views</div>
                </div>
              </div>
              <UpcomingWeek leads={leads} />
              </>
            )}

            {tab === 'landing' && (
              <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4 space-y-3">
                {lp && publicUrl && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-400/40 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[9px] uppercase text-emerald-300 font-black">Your public URL</div>
                      <div className="text-xs font-mono break-all" data-testid="leadgen-public-url">{publicUrl}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={copyLpUrl} data-testid="leadgen-copy-url" className="px-2 py-1 rounded-lg bg-white/10 text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                      <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="leadgen-open-url" className="px-2 py-1 rounded-lg bg-emerald-500 text-black text-xs flex items-center gap-1 font-black"><ExternalLink className="w-3 h-3" /> Open</a>
                    </div>
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-cyan-300 font-black mb-1">Landing Page Details</div>
                <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Display Name / Shop Name" data-testid="leadgen-display-name" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <textarea value={brandDesc} onChange={(e) => setBrandDesc(e.target.value)} placeholder="Brand description (max 400 chars)" maxLength={400} data-testid="leadgen-brand-desc" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" rows={2} />
                <input value={hookOffer} onChange={(e) => setHookOffer(e.target.value)} placeholder="🎁 Hook offer (e.g. Flat 20% off for first 100 leads)" data-testid="leadgen-hook" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <input value={heroUrl} onChange={(e) => setHeroUrl(e.target.value)} placeholder="Hero image URL (optional)" data-testid="leadgen-hero-url" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <input value={customCategories} onChange={(e) => setCustomCategories(e.target.value)} placeholder="Custom categories (comma-separated, e.g. Retail, Salon)" data-testid="leadgen-custom-cat" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
                <div className="flex gap-2 flex-wrap">
                  {['gold', 'rose', 'cyan', 'emerald', 'fuchsia'].map((t) => (
                    <button key={t} onClick={() => setTheme(t)} data-testid={`leadgen-theme-${t}`} className={`px-3 py-1 rounded-lg text-xs font-black ${theme === t ? 'bg-cyan-500 text-black' : 'bg-white/10'}`}>{t}</button>
                  ))}
                </div>
                <button onClick={saveLP} disabled={saving} data-testid="leadgen-save" className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-black font-black">{saving ? 'Saving…' : 'Save Landing Page'}</button>
              </div>
            )}

            {tab === 'leads' && <LeadsDBView leads={leads} />}

            {tab === 'board' && leaderboard && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/10 p-3">
                  <div className="text-xs uppercase tracking-widest text-cyan-300 font-black mb-2">🏆 Top Lead Generators</div>
                  {(leaderboard.top_lead_generators || []).map((r, i) => (
                    <div key={r._id} className="flex items-center justify-between py-1 text-sm"><span>#{i + 1} {r.display_name || r.username}</span><b>{r.count}</b></div>
                  ))}
                </div>
                <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3">
                  <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-2">💰 Top AT Earners</div>
                  {(leaderboard.top_at_earners || []).map((r, i) => (
                    <div key={r._id} className="flex items-center justify-between py-1 text-sm"><span>#{i + 1} {r.username}</span><b>{r.earned.toFixed(2)} AT</b></div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ===== Enhanced Leads DB View with Multi-Filters + Single/Bulk WhatsApp =====
function LeadsDBView({ leads }) {
  const [fCity, setFCity] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fBirth, setFBirth] = useState('');       // month 1-12 or ''
  const [fAnniv, setFAnniv] = useState('');
  const [fEvent, setFEvent] = useState('');
  const [fSearch, setFSearch] = useState('');
  const [selected, setSelected] = useState({});
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTemplate, setMsgTemplate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const uniqueCities = useMemo(() => [...new Set(leads.map((l) => l.city).filter(Boolean))].sort(), [leads]);
  const uniqueCategories = useMemo(() => [...new Set(leads.map((l) => l.business_category).filter(Boolean))].sort(), [leads]);

  const monthOf = (iso) => { try { return String(new Date(iso).getMonth() + 1); } catch { return ''; } };

  const filtered = useMemo(() => leads.filter((l) => {
    if (fCity && l.city !== fCity) return false;
    if (fCategory && l.business_category !== fCategory) return false;
    if (fBirth && (!l.date_of_birth || monthOf(l.date_of_birth) !== fBirth)) return false;
    if (fAnniv && (!l.anniversary || monthOf(l.anniversary) !== fAnniv)) return false;
    if (fEvent && (!l.important_event_date || monthOf(l.important_event_date) !== fEvent)) return false;
    if (fSearch) {
      const s = fSearch.toLowerCase();
      const hay = `${l.full_name} ${l.mobile} ${l.whatsapp} ${l.city} ${l.address}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [leads, fCity, fCategory, fBirth, fAnniv, fEvent, fSearch]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selCount = selectedIds.length;
  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (selCount === filtered.length) setSelected({});
    else setSelected(Object.fromEntries(filtered.map((l) => [l.id, true])));
  };
  const clearFilters = () => { setFCity(''); setFCategory(''); setFBirth(''); setFAnniv(''); setFEvent(''); setFSearch(''); };

  const openMessage = () => {
    if (selCount === 0) { toast.error('Select at least 1 lead'); return; }
    setMsgTemplate(`Namaskar {name} 🙏\n\n[Your message here]\n\nRegards,\n{shop_name}`);
    setMsgOpen(true);
  };

  // ===== 1-Click Personalized Welcome =====
  // Auto-detects today's Birthday / Anniversary from lead data and crafts warm greeting.
  const buildWelcomeMsg = (l) => {
    const name = l.full_name || 'Friend';
    const today = new Date();
    const mmdd = (iso) => { try { const d = new Date(iso); return `${d.getMonth() + 1}-${d.getDate()}`; } catch { return ''; } };
    const todayKey = `${today.getMonth() + 1}-${today.getDate()}`;
    let occasion = '';
    if (l.date_of_birth && mmdd(l.date_of_birth) === todayKey) occasion = `\n\n🎂 *Happy Birthday, ${name}!* Wishing you a year full of joy, prosperity & good health. 🎉`;
    else if (l.anniversary && mmdd(l.anniversary) === todayKey) occasion = `\n\n💍 *Happy Anniversary, ${name}!* May love, laughter & togetherness bloom forever. 🌹`;
    else if (l.important_event_date && mmdd(l.important_event_date) === todayKey) occasion = `\n\n🎉 Special day wishes, ${name}! May today bring you happiness & success.`;
    const city = l.city ? ` from ${l.city}` : '';
    return `Namaskar ${name} 🙏${occasion}\n\nThank you for connecting with us${city}. We're delighted to welcome you to the *Anant* family — your partner for Vyapar (ERP), Samadhan Center & AI Trial Room.\n\nStay tuned for exclusive offers & value-packed updates. Reply anytime — we're here to help. 💫\n\n— Team Anant Web Portal`;
  };

  const sendWelcomeSingle = (l) => {
    const phone = (l.whatsapp || l.mobile || '').replace(/\D/g, '');
    if (!phone) { toast.error('No phone number'); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildWelcomeMsg(l))}`, '_blank');
  };

  const sendWelcomeBulk = () => {
    const selLeads = filtered.filter((l) => selected[l.id]);
    if (selLeads.length === 0) { toast.error('Select at least 1 lead'); return; }
    if (selLeads.length > 25) { toast.error('Max 25 per batch — browser popup limit'); return; }
    let idx = 0;
    const openNext = () => {
      if (idx >= selLeads.length) { toast.success(`✅ ${selLeads.length} personalized welcome messages opened!`); return; }
      const l = selLeads[idx++];
      const phone = (l.whatsapp || l.mobile || '').replace(/\D/g, '');
      if (!phone) { openNext(); return; }
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildWelcomeMsg(l))}`, '_blank');
      setTimeout(openNext, 1200);
    };
    openNext();
  };

  const sendSingle = (l) => {
    const msg = encodeURIComponent(`Namaskar ${l.full_name} 🙏\n\n[Your message]`);
    const phone = (l.whatsapp || l.mobile || '').replace(/\D/g, '');
    if (!phone) { toast.error('No phone number'); return; }
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  // ===== Auto Today-Blast: Birthday + Anniversary + Important Event =====
  const todayMatches = useMemo(() => {
    const t = new Date();
    const key = `${t.getMonth() + 1}-${t.getDate()}`;
    const mmdd = (iso) => { try { const d = new Date(iso); return `${d.getMonth() + 1}-${d.getDate()}`; } catch { return ''; } };
    const bdays = leads.filter((l) => l.date_of_birth && mmdd(l.date_of_birth) === key);
    const annivs = leads.filter((l) => l.anniversary && mmdd(l.anniversary) === key);
    const events = leads.filter((l) => l.important_event_date && mmdd(l.important_event_date) === key);
    // dedupe across categories by id, priority: birthday > anniversary > event
    const seen = new Set();
    const all = [];
    [...bdays, ...annivs, ...events].forEach((l) => { if (!seen.has(l.id)) { seen.add(l.id); all.push(l); } });
    return { bdays, annivs, events, all };
  }, [leads]);

  const blastList = (list, label) => {
    if (list.length === 0) { toast.error(`No ${label} today`); return; }
    if (list.length > 25) { toast.error(`${list.length} matches — max 25 per batch. Please filter or split.`); return; }
    let idx = 0;
    const openNext = () => {
      if (idx >= list.length) { toast.success(`🚀 ${list.length} ${label} blasts opened!`); return; }
      const l = list[idx++];
      const phone = (l.whatsapp || l.mobile || '').replace(/\D/g, '');
      if (!phone) { openNext(); return; }
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildWelcomeMsg(l))}`, '_blank');
      setTimeout(openNext, 1200);
    };
    openNext();
  };

  const sendBulk = () => {
    const selLeads = filtered.filter((l) => selected[l.id]);
    if (selLeads.length === 0) { toast.error('No leads selected'); return; }
    // Open in batches so browser doesn't block popups too aggressively
    let idx = 0;
    const openNext = () => {
      if (idx >= selLeads.length) { toast.success(`Opened ${selLeads.length} WhatsApp chats!`); return; }
      const l = selLeads[idx++];
      const phone = (l.whatsapp || l.mobile || '').replace(/\D/g, '');
      if (!phone) { openNext(); return; }
      const personalized = msgTemplate.replace('{name}', l.full_name || 'Friend').replace('{city}', l.city || '').replace('{shop_name}', 'Anant Web Portal');
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(personalized)}`, '_blank');
      setTimeout(openNext, 1200);   // stagger to avoid popup-block
    };
    openNext();
    setMsgOpen(false);
  };

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-4">
      {/* ===== Today's Auto-Blast Banner (Birthday + Anniversary + Event) ===== */}
      {todayMatches.all.length > 0 && (
        <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/20 via-pink-500/15 to-purple-500/20 p-3 mb-3" data-testid="leadgen-today-blast">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🚀 Today&apos;s Auto-Blast · {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
              <div className="text-xs opacity-90 mt-0.5">Personalized wishes ready — click send, we handle the rest.</div>
            </div>
            <button onClick={() => blastList(todayMatches.all, 'today')} data-testid="leadgen-blast-all" disabled={todayMatches.all.length === 0} className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-400 via-pink-500 to-purple-500 text-black text-xs font-black disabled:opacity-40 flex items-center gap-1">🚀 Blast All ({todayMatches.all.length})</button>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <button onClick={() => blastList(todayMatches.bdays, 'birthday')} disabled={todayMatches.bdays.length === 0} data-testid="leadgen-blast-bday" className="rounded-lg bg-pink-500/25 border border-pink-400/40 p-2 hover:bg-pink-500/40 disabled:opacity-40">
              <div className="text-lg">🎂</div>
              <div className="font-black text-pink-200">{todayMatches.bdays.length}</div>
              <div className="text-[10px] opacity-80">Birthdays</div>
            </button>
            <button onClick={() => blastList(todayMatches.annivs, 'anniversary')} disabled={todayMatches.annivs.length === 0} data-testid="leadgen-blast-anniv" className="rounded-lg bg-rose-500/25 border border-rose-400/40 p-2 hover:bg-rose-500/40 disabled:opacity-40">
              <div className="text-lg">💍</div>
              <div className="font-black text-rose-200">{todayMatches.annivs.length}</div>
              <div className="text-[10px] opacity-80">Anniversaries</div>
            </button>
            <button onClick={() => blastList(todayMatches.events, 'important event')} disabled={todayMatches.events.length === 0} data-testid="leadgen-blast-event" className="rounded-lg bg-amber-500/25 border border-amber-400/40 p-2 hover:bg-amber-500/40 disabled:opacity-40">
              <div className="text-lg">🎉</div>
              <div className="font-black text-amber-200">{todayMatches.events.length}</div>
              <div className="text-[10px] opacity-80">Events</div>
            </button>
          </div>
          <div className="text-[10px] opacity-70 mt-2">💡 Each blast opens personalized wa.me tabs (1.2s stagger). Max 25/batch. Zero API cost.</div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-cyan-300 font-black">Your Database ({filtered.length}/{leads.length})</div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowFilters((v) => !v)} data-testid="leadgen-toggle-filters" className="px-2 py-1 rounded-lg bg-white/10 text-xs flex items-center gap-1"><Filter className="w-3 h-3" /> Filters</button>
          <button onClick={sendWelcomeBulk} disabled={selCount === 0} data-testid="leadgen-bulk-welcome" title="Auto-personalized (name + today's birthday/anniversary wish + tagline). Max 25 at once." className="px-2 py-1 rounded-lg bg-gradient-to-r from-amber-400 to-pink-500 text-black text-xs font-black flex items-center gap-1 disabled:opacity-40">🎉 Welcome ({selCount})</button>
          <button onClick={openMessage} disabled={selCount === 0} data-testid="leadgen-bulk-wa" className="px-2 py-1 rounded-lg bg-green-500 text-black text-xs font-black flex items-center gap-1 disabled:opacity-40"><MessageCircle className="w-3 h-3" /> Bulk WhatsApp ({selCount})</button>
        </div>
      </div>

      {showFilters && (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 mb-3 space-y-2" data-testid="leadgen-filters">
          <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="🔍 Search name/mobile/city…" data-testid="leadgen-filter-search" className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-xs" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <select value={fCity} onChange={(e) => setFCity(e.target.value)} data-testid="leadgen-filter-city" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-xs">
              <option value="">All Cities</option>{uniqueCities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} data-testid="leadgen-filter-cat" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-xs">
              <option value="">All Categories</option>{uniqueCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={fBirth} onChange={(e) => setFBirth(e.target.value)} data-testid="leadgen-filter-birth" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-xs">
              <option value="">Birth Month (Any)</option>{[...Array(12)].map((_, i) => <option key={i} value={i + 1}>🎂 {new Date(2000, i, 1).toLocaleString('en', { month: 'long' })}</option>)}
            </select>
            <select value={fAnniv} onChange={(e) => setFAnniv(e.target.value)} data-testid="leadgen-filter-anniv" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-xs">
              <option value="">Anniversary (Any)</option>{[...Array(12)].map((_, i) => <option key={i} value={i + 1}>💍 {new Date(2000, i, 1).toLocaleString('en', { month: 'long' })}</option>)}
            </select>
            <select value={fEvent} onChange={(e) => setFEvent(e.target.value)} data-testid="leadgen-filter-event" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-600 text-xs">
              <option value="">Event Month (Any)</option>{[...Array(12)].map((_, i) => <option key={i} value={i + 1}>🎉 {new Date(2000, i, 1).toLocaleString('en', { month: 'long' })}</option>)}
            </select>
            <button onClick={clearFilters} data-testid="leadgen-filter-clear" className="px-2 py-1.5 rounded bg-rose-500/40 text-xs">Clear All</button>
          </div>
          <button onClick={() => toast.success(`Showing ${filtered.length} of ${leads.length} leads`)} data-testid="leadgen-filter-apply" className="w-full px-3 py-2 rounded-lg bg-cyan-500 text-black text-xs font-black">🔎 Apply / Search Filters</button>
        </div>
      )}

      {filtered.length === 0 ? <div className="text-sm text-slate-400 text-center py-6">No leads match filters.</div> : (
        <>
          <div className="flex items-center gap-2 mb-2">
            <label className="text-xs flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={selCount === filtered.length && filtered.length > 0} onChange={toggleAll} data-testid="leadgen-select-all" /> Select all ({selCount}/{filtered.length})</label>
          </div>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filtered.map((l) => (
              <div key={l.id} className={`p-3 rounded-lg border ${selected[l.id] ? 'border-cyan-400 bg-cyan-500/10' : 'border-slate-700 bg-slate-800'}`} data-testid={`leadgen-lead-${l.id}`}>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={!!selected[l.id]} onChange={() => toggleSel(l.id)} data-testid={`leadgen-select-${l.id}`} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between flex-wrap gap-1">
                      <div className="min-w-0"><b>{l.full_name}</b> · <span className="font-mono">{l.mobile}</span></div>
                      <div className="flex gap-1">
                        <button onClick={() => sendWelcomeSingle(l)} data-testid={`leadgen-welcome-${l.id}`} title="1-Click personalized welcome (auto DOB/Anniv wish if today)" className="px-2 py-0.5 rounded bg-gradient-to-r from-amber-400 to-pink-500 text-black text-[10px] font-black">🎉 Welcome</button>
                        <button onClick={() => sendSingle(l)} data-testid={`leadgen-wa-${l.id}`} className="px-2 py-0.5 rounded bg-green-500 text-black text-[10px] font-black flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WA</button>
                      </div>
                    </div>
                    <div className="text-xs opacity-80 grid grid-cols-1 sm:grid-cols-2 gap-x-3">
                      <span>📍 {l.city} · {l.business_category}</span>
                      <span className="opacity-70">📱 WA: {l.whatsapp}</span>
                      {l.date_of_birth && <span>🎂 {l.date_of_birth}</span>}
                      {l.anniversary && <span>💍 {l.anniversary}</span>}
                      {l.important_event_date && <span>🎉 {l.important_event_date}</span>}
                    </div>
                    <div className="text-[10px] opacity-60 mt-1">{l.address}</div>
                    {l.special_note && <div className="text-[10px] text-cyan-300 mt-1">📝 {l.special_note}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Bulk message modal */}
      {msgOpen && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-3" onClick={() => setMsgOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl border-2 border-green-400/40 bg-slate-900 p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="leadgen-bulk-modal">
            <div className="flex items-center justify-between">
              <div className="text-lg font-black text-green-300">📨 Bulk WhatsApp ({selCount})</div>
              <button onClick={() => setMsgOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs text-slate-300">Message template. Placeholders: <code className="text-cyan-300">{'{name}'}</code>, <code className="text-cyan-300">{'{city}'}</code>, <code className="text-cyan-300">{'{shop_name}'}</code>. Each chat opens in new tab (1.2s stagger).</div>
            <textarea value={msgTemplate} onChange={(e) => setMsgTemplate(e.target.value)} rows={6} data-testid="leadgen-bulk-message" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-sm" />
            <div className="flex gap-2 flex-wrap">
              <button onClick={sendBulk} data-testid="leadgen-bulk-send" className="flex-1 px-4 py-3 rounded-xl bg-green-500 text-black font-black flex items-center justify-center gap-1"><Send className="w-4 h-4" /> Open {selCount} WhatsApp chats</button>
            </div>
            <div className="text-[10px] text-amber-200 opacity-80">⚠️ Allow pop-ups. Zero-cost — uses wa.me links, no external API.</div>
          </div>
        </div>
      )}
    </div>
  );
}


// ===== Upcoming This Week (next 7 days) =====
function UpcomingWeek({ leads }) {
  const days = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const mmdd = (iso) => { try { const d = new Date(iso); return `${d.getMonth() + 1}-${d.getDate()}`; } catch { return ''; } };
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      const key = `${d.getMonth() + 1}-${d.getDate()}`;
      const bdays = leads.filter((l) => l.date_of_birth && mmdd(l.date_of_birth) === key);
      const annivs = leads.filter((l) => l.anniversary && mmdd(l.anniversary) === key);
      const events = leads.filter((l) => l.important_event_date && mmdd(l.important_event_date) === key);
      const total = bdays.length + annivs.length + events.length;
      arr.push({ date: d, key, bdays, annivs, events, total });
    }
    return arr;
  }, [leads]);

  const grandTotal = days.reduce((s, d) => s + d.total, 0);

  const buildShareText = () => {
    const lines = ['🗓️ *Upcoming This Week — Anant LeadGen*', ''];
    days.forEach((d) => {
      if (d.total === 0) return;
      const label = d.date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
      const parts = [];
      if (d.bdays.length) parts.push(`🎂 ${d.bdays.length}`);
      if (d.annivs.length) parts.push(`💍 ${d.annivs.length}`);
      if (d.events.length) parts.push(`🎉 ${d.events.length}`);
      lines.push(`• *${label}* — ${parts.join(' · ')}`);
      [...d.bdays, ...d.annivs, ...d.events].forEach((l) => {
        const tag = d.bdays.includes(l) ? '🎂' : d.annivs.includes(l) ? '💍' : '🎉';
        lines.push(`   ${tag} ${l.full_name}${l.city ? ' · ' + l.city : ''}`);
      });
    });
    if (grandTotal === 0) lines.push('_No upcoming birthdays / anniversaries / events this week._');
    lines.push('', '— powered by Anant LeadGen');
    return lines.join('\n');
  };

  const shareIt = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try { await navigator.share({ title: 'Upcoming This Week', text }); return; } catch { /* fall through */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const copyIt = async () => {
    try { await navigator.clipboard.writeText(buildShareText()); toast.success('Copied to clipboard!'); } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="rounded-2xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-500/10 via-blue-500/5 to-purple-500/10 p-4 mt-3" data-testid="leadgen-upcoming-week">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🗓️ Upcoming This Week</div>
          <div className="text-xs opacity-90 mt-0.5">Next 7 days — plan your outreach in advance ({grandTotal} total)</div>
        </div>
        <div className="flex gap-2">
          <button onClick={copyIt} data-testid="leadgen-upcoming-copy" className="px-2 py-1 rounded-lg bg-white/10 text-xs font-black flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
          <button onClick={shareIt} data-testid="leadgen-upcoming-share" className="px-3 py-1 rounded-lg bg-gradient-to-r from-cyan-400 to-blue-500 text-black text-xs font-black flex items-center gap-1"><Send className="w-3 h-3" /> Share</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-7 gap-2">
        {days.map((d, i) => (
          <div key={i} className={`rounded-lg border p-2 text-center ${d.total > 0 ? 'border-cyan-400/50 bg-cyan-500/15' : 'border-slate-700 bg-slate-800/40 opacity-60'}`} data-testid={`leadgen-upcoming-day-${i}`}>
            <div className="text-[9px] uppercase opacity-70">{d.date.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
            <div className="text-sm font-black">{d.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
            <div className="flex justify-center gap-1.5 mt-1 text-[11px] flex-wrap">
              {d.bdays.length > 0 && <span className="text-pink-300" title={d.bdays.map((l) => l.full_name).join(', ')}>🎂 {d.bdays.length}</span>}
              {d.annivs.length > 0 && <span className="text-rose-300" title={d.annivs.map((l) => l.full_name).join(', ')}>💍 {d.annivs.length}</span>}
              {d.events.length > 0 && <span className="text-amber-300" title={d.events.map((l) => l.full_name).join(', ')}>🎉 {d.events.length}</span>}
              {d.total === 0 && <span className="opacity-40">—</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
